import React, { useState, useMemo } from 'react';
import { FaEdit, FaTrash, FaSearch } from 'react-icons/fa';

const TacticsList = ({ tactics, onEdit, onDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  const filteredTactics = useMemo(() => {
    if (!searchTerm.trim()) return tactics;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return tactics.filter(tactic => 
      tactic.tactic_name.toLowerCase().includes(lowerSearchTerm) ||
      tactic.tactic_category.toLowerCase().includes(lowerSearchTerm) ||
      (tactic.tactic_slug && tactic.tactic_slug.toLowerCase().includes(lowerSearchTerm))
    );
  }, [tactics, searchTerm]);

  const totalPages = Math.ceil(filteredTactics.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTactics = filteredTactics.slice(startIndex, startIndex + itemsPerPage);

  const getCategoryColor = (category) => {
    const colors = {
      'Exercise': 'bg-blue-100 text-blue-800',
      'Diet & Nutrition': 'bg-green-100 text-green-800',
      'Life Style': 'bg-purple-100 text-purple-800',
      'Supplements': 'bg-orange-100 text-orange-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Search Box */}
      <div className="p-4 border-b">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by tactic name, category, or slug..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-600 mt-2">
            Found {filteredTactics.length} tactic{filteredTactics.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Tactic Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Biomarkers</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Goals</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTactics.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  No tactics found
                </td>
              </tr>
            ) : (
              currentTactics.map((tactic) => (
                <tr key={tactic.id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{tactic.tactic_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(tactic.tactic_category)}`}>
                      {tactic.tactic_category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {tactic.impacted_biomarkers && tactic.impacted_biomarkers.length > 0 ? (
                        tactic.impacted_biomarkers.slice(0, 2).map((biomarker, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                            {typeof biomarker === 'object' ? biomarker.name : biomarker}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                      {tactic.impacted_biomarkers && tactic.impacted_biomarkers.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          +{tactic.impacted_biomarkers.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {tactic.impacted_goals && tactic.impacted_goals.length > 0 ? (
                        tactic.impacted_goals.slice(0, 2).map((goal, idx) => (
                          <span key={idx} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded">
                            {typeof goal === 'object' ? goal.name : goal}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                      {tactic.impacted_goals && tactic.impacted_goals.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          +{tactic.impacted_goals.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(tactic)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(tactic.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 py-4 border-t">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 border rounded ${
                currentPage === page
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TacticsList;
